/**
 * Full Width Video: parallax on placeholder image (like video-hero) and click-to-replace with video.
 */
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

const PARALLAX_STRENGTH = 15;

/**
 * Adds provider-specific autoplay flags so the iframe begins playback after the user clicks play.
 * (Embeds are stored without autoplay; loading on click is user-gesture-safe for most providers.)
 */
function embedSrcWithAutoplay(rawSrc) {
	if (!rawSrc || typeof rawSrc !== "string") {
		return rawSrc;
	}
	let url;
	try {
		url = new URL(rawSrc, window.location.href);
	} catch {
		return rawSrc;
	}
	const host = url.hostname.replace(/^www\./i, "").toLowerCase();

	const youtubeHosts = ["youtube.com", "youtube-nocookie.com", "youtu.be"];
	const isYoutube = youtubeHosts.some((h) => host === h || host.endsWith("." + h));

	if (isYoutube) {
		url.searchParams.set("autoplay", "1");
		return url.toString();
	}

	if (host.includes("vimeo.com")) {
		url.searchParams.set("autoplay", "1");
		return url.toString();
	}

	if (host.includes("dailymotion.com") || host === "dai.ly") {
		url.searchParams.set("autoplay", "1");
		return url.toString();
	}

	if (host.includes("wistia.com") || host.includes("wistia.net")) {
		url.searchParams.set("autoPlay", "true");
		return url.toString();
	}

	return rawSrc;
}

export function initFullWidthVideo() {
	const blocks = document.querySelectorAll(".full-width-video");
	if (!blocks.length) return;

	blocks.forEach((block) => {
		const parallaxEl = block.querySelector(".js-full-width-video-parallax");
		const placeholderWrap = block.querySelector(".js-full-width-video-placeholder");
		const contentWrap = block.querySelector(".js-full-width-video-content");
		const openBtn = block.querySelector(".js-full-width-video-open");

		// Parallax on placeholder (same as video-hero)
		if (parallaxEl) {
			gsap.to(parallaxEl, {
				yPercent: PARALLAX_STRENGTH,
				ease: "none",
				scrollTrigger: {
					trigger: block,
					start: "top top",
					end: "bottom top",
					scrub: 0.5,
				},
			});
		}

		// Click: replace placeholder with video
		if (openBtn && placeholderWrap && contentWrap) {
			openBtn.addEventListener("click", () => {
				const type = openBtn.getAttribute("data-video-type");
				const mp4 = openBtn.getAttribute("data-video-mp4") || "";

				contentWrap.innerHTML = "";

				if (type === "mp4" && mp4) {
					const video = document.createElement("video");
					video.src = mp4;
					video.controls = true;
					video.autoplay = true;
					video.setAttribute("playsinline", "");
					video.classList.add("full-width-video__video");
					contentWrap.appendChild(video);
					video.play().catch(() => {});
				} else if (type === "embed") {
					const template = block.querySelector(".js-full-width-video-embed");
					const embedHtml = template ? template.textContent.trim() : "";
					if (embedHtml) {
						const wrap = document.createElement("div");
						wrap.className = "full-width-video__embed-wrap";
						wrap.innerHTML = embedHtml;
						wrap.querySelectorAll("iframe").forEach((iframe) => {
							const src = iframe.getAttribute("src");
							if (src) {
								iframe.setAttribute("src", embedSrcWithAutoplay(src));
							}
						});
						contentWrap.appendChild(wrap);
					}
				}

				placeholderWrap.style.display = "none";
				placeholderWrap.setAttribute("aria-hidden", "true");
				contentWrap.hidden = false;
				contentWrap.removeAttribute("aria-hidden");
			});
		}
	});
}
